import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton } from '@mui/material';
import { Fragment, useCallback, useContext } from 'react';
import { themeVars } from 'src/ui-components/theme/theme';

import sidebar from '../../assets/icons/menu/sidebar.svg';
import logo from '../../assets/logo_icon_white.svg';
import BrandSelect from '../../components/header/BrandSelect';
import { AuthContext } from '../../contexts/AuthContext';
import { BrandContext } from '../../contexts/BrandContext';
import { FrontMenuItem } from './components/FrontMenuItem';
import { FrontMenuItemChild } from './components/FrontMenuItemChild';
import { MyAccount } from './components/MyAccount';
import { WhatsNew } from './components/WhatsNew';
import { useGetData } from './hooks/useGetData';
import { useSidebarStore } from './store/store';

export const FrontMenu = () => {
  const { collapsed, setCollapsed } = useSidebarStore();
  const { setSelectedBrand, brand } = useContext(BrandContext);
  const { apiUser } = useContext(AuthContext);
  const { menuItems } = useGetData();

  const onBrandSelectChange = useCallback(
    (newValue: { brandId: string }) => {
      setSelectedBrand(newValue.brandId);
    },
    [setSelectedBrand],
  );

  return (
    <Wrapper $isCollapsed={collapsed}>
      <TopWrapper>
        <Header $isCollapsed={collapsed}>
          {!collapsed && <Logo src={logo} />}
          <IconButton size={'small'} onClick={() => setCollapsed(!collapsed)}>
            <Sidebar />
          </IconButton>
        </Header>
        {!collapsed && (
          <>
            <BrandWrapper>
              <BrandSelect dropdownOnly={true} brand={brand} onChange={onBrandSelectChange} />
            </BrandWrapper>
            <Name>Hi {apiUser?.name} </Name>
          </>
        )}
        <MenuWrapper $isCollapsed={collapsed}>
          {menuItems?.map((item, key) => (
            <Fragment key={key}>
              {!item.children ? (
                <FrontMenuItem item={item} />
              ) : (
                <div>
                  <FrontMenuItem item={item} />
                  {!collapsed && <FrontMenuItemChild item={item} />}
                </div>
              )}
            </Fragment>
          ))}
        </MenuWrapper>
      </TopWrapper>
      <Footer>
        <MyAccount />
        <WhatsNew />
      </Footer>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $isCollapsed?: boolean }>`
  font-family: ${themeVars.fonts.telka};
  display: flex;
  overflow: auto;
  width: 256px;
  height: 100vh;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-shrink: 0;
  align-self: stretch;
  transition: width 0.3s;
  background: ${themeVars.colors.titan100};
  user-select: none;
  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      width: 48px;
      padding: 24px 8px;
    `};
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  width: 100%;
`;

const Header = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      justify-content: center;
    `};
`;

const Logo = styled.img``;

const Sidebar = styled.div`
  width: 24px;
  height: 24px;
  background: url(${sidebar}) no-repeat center;
  mask: url(${sidebar}) no-repeat center;
  background: #65665e;
`;

const BrandWrapper = styled.div`
  width: 100%;
`;

const Name = styled.div`
  width: 100%;
  font-size: 18px;
  font-weight: 400;
  color: #000;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-family: ${themeVars.fonts.telkaExtended};
`;

const MenuWrapper = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
